import { t } from './i18n.js';
import { useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { send } from './api.js';
import { Notice } from './components.js';
import { mfaAllMembers } from '../shared/mfa-policy.js';
import type { Bootstrap } from '../shared/types.js';

/** Organisation setting: second factor for every member, beyond the administrators who always have it. */
export function MfaPolicy({
  organization,
  notify,
  onChange,
}: {
  organization: Bootstrap['organization'];
  notify: (s: string) => void;
  onChange?: (policy: Record<string, unknown>) => void;
}) {
  const [policy, setPolicy] = useState<Record<string, unknown>>(organization.policy ?? {}),
    [version, setVersion] = useState(organization.policy_version ?? 0),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  async function change(all: boolean) {
    setBusy(true);
    setError('');
    try {
      const r = await send<{ policy: Record<string, unknown>; policy_version: number }>(
        `/security-policies/${organization.id}`,
        { ...policy, mfaAllMembers: all },
        'PATCH',
        { 'If-Match': `"${version}"` },
      );
      setPolicy(r.policy);
      setVersion(r.policy_version);
      onChange?.(r.policy);
      notify(
        all
          ? t('Double authentification exigée pour tous les membres.')
          : t('Double authentification facultative pour les utilisateurs.'),
      );
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="mfa-policy">
      <ShieldCheck size={22} />
      <div>
        <h3>{t('Double authentification')}</h3>
        <p className="muted">
          {t(
            'Toujours exigée pour les administrateurs. Facultative pour les autres membres, sauf si vous l’exigez pour tous : ceux qui ne l’ont pas la configureront à leur prochaine connexion.',
          )}
        </p>
        {error && <Notice type="error">{error}</Notice>}
      </div>
      <label>
        <input
          type="checkbox"
          checked={mfaAllMembers(policy)}
          disabled={busy}
          onChange={(e) => void change(e.target.checked)}
        />
        {t('Exiger pour tous les membres')}
      </label>
    </section>
  );
}
